import { useParams, Link } from "react-router-dom";
import { Phone, ArrowRight, CheckCircle, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import SEO from "@/components/SEO";
import ContactForm from "@/components/ContactForm";
import FAQ from "@/components/FAQ";
import { renderSection } from "@/components/ServiceSections";
import { plumbingContent } from "@/data/plumbing-content";
import { PHONE, PHONE_LINK, PLUMBING_SERVICES, BUSINESS_NAME, AREA_SERVED_SCHEMA, SOCIAL_PROFILES } from "@/lib/constants";

const ServicePage = () => {
  const { serviceSlug } = useParams<{ serviceSlug: string }>();
  const service = PLUMBING_SERVICES.find((s) => s.slug === serviceSlug);
  const content = serviceSlug ? plumbingContent[serviceSlug] : null;

  const defaultContent = {
    title: service?.title || "Plumbing Service",
    metaDesc: `Professional ${service?.title?.toLowerCase()} in Tolleson, AZ. Licensed plumbers, quality work. Call (480) 322-2567.`,
    heroDesc: `${BUSINESS_NAME} provides expert ${service?.title?.toLowerCase()} services for homes and businesses in Tolleson and the West Valley.`,
    benefits: [
      "Licensed and insured technicians",
      "Upfront, honest pricing",
      "Quality parts and materials",
      "Satisfaction guaranteed",
    ],
    faqs: [] as { question: string; answer: string }[],
    sections: [],
  };

  const pageContent = content || defaultContent;

  if (!service) {
    return (
      <div className="container py-24 text-center">
        <h1 className="text-3xl font-bold mb-4">Service Not Found</h1>
        <Link to="/services/">
          <Button variant="outline">View All Services</Button>
        </Link>
      </div>
    );
  }

  const baseUrl = "https://truamericanplumbing.com";

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: service.title,
    provider: { "@type": "Plumber", "@id": `${baseUrl}/#organization`, sameAs: SOCIAL_PROFILES },
    areaServed: AREA_SERVED_SCHEMA,
    url: `${baseUrl}/services/${serviceSlug}`,
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: baseUrl },
      { "@type": "ListItem", position: 2, name: "Services", item: `${baseUrl}/services` },
      { "@type": "ListItem", position: 3, name: service.title, item: `${baseUrl}/services/${serviceSlug}` },
    ],
  };

  const faqSchema = pageContent.faqs.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: pageContent.faqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: { "@type": "Answer", text: faq.answer },
    })),
  } : null;

  const schemas = [serviceSchema, breadcrumbSchema, ...(faqSchema ? [faqSchema] : [])];

  return (
    <>
      <SEO
        title={pageContent.title}
        description={pageContent.metaDesc}
        canonicalUrl={`/services/${serviceSlug}`}
        schema={schemas}
      />

      {/* Breadcrumb */}
      <div className="bg-muted/30 border-b border-border">
        <div className="container py-3">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Link to="/" className="hover:text-primary">Home</Link>
            <span>/</span>
            <Link to="/services/" className="hover:text-primary">Services</Link>
            <span>/</span>
            <span className="text-foreground">{service.title}</span>
          </div>
        </div>
      </div>

      {/* Hero */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-muted/50 to-background">
        <div className="container">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              <span className="text-gradient-copper">{pageContent.title}</span>
            </h1>
            <p className="text-xl text-muted-foreground mb-8">
              {pageContent.heroDesc}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href={PHONE_LINK}>
                <Button variant="hero" size="xl" className="w-full sm:w-auto gap-2">
                  <Phone className="h-5 w-5" />
                  Call {PHONE}
                </Button>
              </a>
              <Link to="/contact/">
                <Button variant="heroOutline" size="xl" className="w-full sm:w-auto gap-2">
                  Get a Quote
                  <ArrowRight className="h-5 w-5" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Dynamic content sections */}
      {pageContent.sections.map((section, index) => renderSection(section, index))}

      {/* Benefits + Contact */}
      <section className="py-16 md:py-24">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <h2 className="text-3xl font-bold mb-6">
                Why Choose Us for{" "}
                <span className="text-gradient-copper">{service.title}</span>
              </h2>
              <div className="space-y-4">
                {pageContent.benefits.map((benefit, index) => (
                  <div key={index} className="flex gap-3">
                    <CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                    <p className="text-foreground/80">{benefit}</p>
                  </div>
                ))}
              </div>

              <div className="mt-8 p-6 bg-card border border-border rounded-xl">
                <h3 className="font-bold text-lg mb-2">Serving the West Valley</h3>
                <p className="text-muted-foreground text-sm">
                  We provide {service.title.toLowerCase()} services in Tolleson,
                  Avondale, Goodyear, Glendale, Peoria, Surprise, Phoenix, and
                  surrounding areas.
                </p>
              </div>
            </div>
            <div>
              <h3 className="text-2xl font-bold mb-6">Request Service</h3>
              <ContactForm />
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      {pageContent.faqs.length > 0 && <FAQ faqs={pageContent.faqs} />}

      {/* Back */}
      <section className="py-8 border-t border-border">
        <div className="container">
          <Link
            to="/services/"
            className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to All Services
          </Link>
        </div>
      </section>
    </>
  );
};

export default ServicePage;
